import React, { useState, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import { useResponsive } from '@/hooks/common/useResponsive';
import { usePmsIntegrationEnabled } from '@/features/payments/hooks/usePmsIntegrationEnabled';
import { UndergoingMaintenancePage } from '@/components/residents/maintenance/UndergoingMaintenancePage';
import { useAppSelector } from '@/store';
import { useMaintenanceTickets } from '@/hooks/residents/useMaintenanceTickets';
import { useNavigation } from '@react-navigation/native';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import type { DesktopMainParamList } from '@/navigation/residents/desktop/DesktopNavigator';
import type { StackNavigationProp } from '@react-navigation/stack';
import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';
import { useFindConversationBySubject } from '@/hooks/residents/useMessages';

import {
  DesktopMaintenanceListView,
  DesktopCreateMaintenanceView,
  DesktopMaintenanceDetailPanel,
} from '@/components/residents/maintenance/desktop';

type DesktopViewMode = 'list' | 'create';

export const MaintenanceScreen: React.FC = () => {
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const pmsEnabled = usePmsIntegrationEnabled();
  const user = useAppSelector((s) => s.auth.user as any);
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const { openTickets, closedTickets, loading, error, refresh } = useMaintenanceTickets();
  const { findBySubject, isSearching } = useFindConversationBySubject();

  const [desktopViewMode, setDesktopViewMode] = useState<DesktopViewMode>('list');
  const [selectedTicket, setSelectedTicket] = useState<IMaintenanceListResponse | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);

  const handleCreateTicket = () => {
    if (isDesktop) {
      setDesktopViewMode('create');
    } else {
      navigation.navigate('CreateMaintenanceTicket');
    }
  };

  const handleCloseCreate = useCallback(() => {
    setDesktopViewMode('list');
  }, []);

  const handleTicketPress = useCallback((ticket: IMaintenanceListResponse) => {
    setSelectedTicket(ticket);
    setDetailVisible(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    // Keep the ticket set so the panel renders its outgoing content while
    // GlassSidePanel plays the slide-out; the panel unmounts the content
    // itself once the exit animation finishes (mirrors ResourceGlassPanel).
    setDetailVisible(false);
  }, []);

  const handleStartChat = useCallback(async () => {
    if (!selectedTicket) return;
    const subjectPrefix = `WO-${selectedTicket.maintenanceRequestId}`;
    const subject = `${subjectPrefix}: ${selectedTicket.maintenanceDescription?.substring(0, 50) || 'Maintenance Request'}`;

    const existingConversationId = await findBySubject(subjectPrefix);

    if (existingConversationId) {
      (navigation as StackNavigationProp<DesktopMainParamList>).navigate('Messages', { existingConversationId });
    } else {
      (navigation as StackNavigationProp<DesktopMainParamList>).navigate('Messages', { initialSubject: subject });
    }
  }, [selectedTicket, navigation, findBySubject]);

  if (!pmsEnabled) {
    return <UndergoingMaintenancePage variant="disabled" />;
  }

  if (user?.hasFutureLease) {
    return <UndergoingMaintenancePage variant="future" />;
  }

  if (!isDesktop) {
    // Non-desktop path handled by MobileMaintenanceScreen in the mobile navigator.
    // This fallback should not normally render but is kept for safety.
    return null;
  }

  if (desktopViewMode === 'create') {
    return (
      <View style={styles.desktopContainer}>
        <DesktopCreateMaintenanceView
          onClose={handleCloseCreate}
          onSuccess={refresh}
        />
      </View>
    );
  }

  return (
    <View style={styles.desktopContainer}>
      <DesktopMaintenanceListView
        openTickets={openTickets ?? []}
        closedTickets={closedTickets ?? []}
        loading={loading}
        error={error ? (typeof error === 'string' ? error : 'An error occurred') : null}
        onNewRequest={handleCreateTicket}
        onTicketPress={handleTicketPress}
      />

      <DesktopMaintenanceDetailPanel
        ticket={selectedTicket}
        visible={detailVisible}
        onClose={handleCloseDetail}
        onStartChat={handleStartChat}
        chatLoading={isSearching}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  desktopContainer: {
    flex: 1,
    backgroundColor: 'transparent',
  },
});
